import { compact } from "lodash";
import { useCallback, useContext, useMemo } from "react";
import { PerformanceAssessmentAnswerFilter } from "types/graphql-schema";

import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import { assertNonNull } from "@helpers/helpers";

import { isAssessmentReportAnswerType } from "./helpers";
import { getNineBoxChartLabel } from "./nine-box/helpers";
import NineBoxCell from "./nine-box/nine-box-cell";
import NineBoxGrid, {
  SingleQuestionNineBoxGrid,
} from "./nine-box/nine-box-grid";
import {
  NineBoxAssessmentAnswer,
  NineBoxPerson,
  NineBoxQuestion,
} from "./nine-box/types";
import {
  getFilterByType,
  matchesPerformanceAssessmentFilter,
} from "./report-filters/helpers";
import { AssessmentReportFilterType } from "./report-filters/types";

/**
 * The nine-box grid of a review's answers. With `showPeople`, each box also
 * lists its people: only the Calibration tab asks for them.
 */
const PerformanceAssessmentChart = ({
  onChangeAnswerFilterList,
  questions,
  showPeople = false,
}: {
  questions: NineBoxQuestion[];
  showPeople?: boolean;
  onChangeAnswerFilterList: (
    answerFilterList: PerformanceAssessmentAnswerFilter[],
  ) => void;
}) => {
  const { userRows, chartLabels, viewOptions } = useContext(
    PerformanceAssessmentReportContext,
  );
  const {
    userFilterList,
    reportsToFilterList,
    teamFilterList,
    enrollmentDateFilterList,
    roleFilterList,
    careerTrackFilterList,
    careerTrackLevelFilterList,
    talentIndicatorFilterList,
    answerFilterList,
    filterMatchType,
  } = useMemo(() => {
    const filterItems = viewOptions.filters.list;
    const userFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.USER,
    );
    const enrollmentDateFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.ENROLLMENT_DATE,
    );
    const reportsToFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.REPORTS_TO,
    );
    const answerFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.ANSWER,
    );
    const talentIndicatorFilterList = filterItems
      .filter(
        (item) => item.type === AssessmentReportFilterType.TALENT_INDICATOR,
      )
      .map((item) => item.data);
    const teamFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.TEAM,
    );
    const roleFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.ROLE,
    );
    const careerTrackFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.CAREER_TRACK,
    );
    const careerTrackLevelFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.CAREER_TRACK_LEVEL,
    );
    return {
      userFilterList,
      reportsToFilterList,
      teamFilterList,
      enrollmentDateFilterList,
      roleFilterList,
      careerTrackFilterList,
      careerTrackLevelFilterList,
      talentIndicatorFilterList,
      answerFilterList,
      filterMatchType: viewOptions.filters.type,
    };
  }, [viewOptions.filters]);

  const handleUpdateAnswerFilter = useCallback(
    (
      xFilter: PerformanceAssessmentAnswerFilter,
      yFilter?: PerformanceAssessmentAnswerFilter,
    ) => {
      onChangeAnswerFilterList(compact([xFilter, yFilter]));
    },
    [onChangeAnswerFilterList],
  );

  const xQuestion = useMemo(
    () =>
      questions.length > 0
        ? questions[viewOptions.nineBox.selectedXQuestionIndex]
        : null,
    [questions, viewOptions.nineBox.selectedXQuestionIndex],
  );
  const yQuestion = useMemo(
    () =>
      questions.length > 1 &&
      viewOptions.nineBox.selectedYQuestionIndex !== null
        ? questions[viewOptions.nineBox.selectedYQuestionIndex]
        : null,
    [questions, viewOptions.nineBox.selectedYQuestionIndex],
  );

  const filteredUserRows = useMemo(() => {
    return userRows.filter((reportRow) => {
      return matchesPerformanceAssessmentFilter({
        userFilterList,
        reportsToFilterList,
        teamFilterList,
        enrollmentDateFilterList,
        roleFilterList,
        careerTrackFilterList,
        careerTrackLevelFilterList,
        talentIndicatorFilterList,
        reportRow,
        matchType: filterMatchType,
      });
    });
  }, [
    userRows,
    filterMatchType,
    reportsToFilterList,
    userFilterList,
    teamFilterList,
    roleFilterList,
    careerTrackFilterList,
    careerTrackLevelFilterList,
    talentIndicatorFilterList,
    enrollmentDateFilterList,
  ]);

  const assessments = useMemo(
    () => compact(filteredUserRows.map((row) => row.assessment)),
    [filteredUserRows],
  );
  const personByAssessmentId = useMemo(
    () =>
      new Map<number, NineBoxPerson>(
        filteredUserRows.flatMap((row) =>
          row.assessment ? [[row.assessment.id, row.user]] : [],
        ),
      ),
    [filteredUserRows],
  );
  const peopleOf = (answers: NineBoxAssessmentAnswer[]) =>
    showPeople
      ? compact(
          answers.map((answer) =>
            personByAssessmentId.get(answer.assessmentId),
          ),
        )
      : undefined;

  const xQuestionAnswers = useMemo(
    () =>
      xQuestion && assessments.length > 0
        ? assessments.reduce((ansList, assessment) => {
            const answers = assessment.answers;
            return ansList.concat(
              compact(
                answers
                  .filter((a) => a?.questionId === xQuestion.id)
                  .map((a) =>
                    isAssessmentReportAnswerType(a)
                      ? {
                          assessmentId: assessment.id,
                          questionId: a.questionId,
                          integerAnswer: assertNonNull(a.integerAnswer),
                        }
                      : null,
                  ),
              ),
            );
          }, [] as NineBoxAssessmentAnswer[])
        : [],
    [xQuestion, assessments],
  );
  const yQuestionAnswers = useMemo(
    () =>
      yQuestion && assessments.length > 0
        ? assessments.reduce((ansList, assessment) => {
            const answers = assessment.answers;
            return ansList.concat(
              compact(
                answers
                  .filter((a) => a?.questionId === yQuestion.id)
                  .map((a) =>
                    isAssessmentReportAnswerType(a)
                      ? {
                          assessmentId: assessment.id,
                          questionId: a.questionId,
                          integerAnswer: assertNonNull(a.integerAnswer),
                        }
                      : null,
                  ),
              ),
            );
          }, [] as NineBoxAssessmentAnswer[])
        : [],
    [yQuestion, assessments],
  );

  if (questions.length === 0 || userRows.length === 0) {
    return null;
  }

  return (
    <div className="p-6 rounded-lg mb-4 bg-gray-100">
      {xQuestion && yQuestion && (
        <NineBoxGrid
          xQuestion={xQuestion}
          yQuestion={yQuestion}
          cellRenderer={({ xValue, yValue }) => {
            const xAnswers = xQuestionAnswers.filter(
              (answer) => answer.integerAnswer === xValue,
            );
            const yAnswerIds = yQuestionAnswers
              .filter((answer) => answer.integerAnswer === yValue)
              .map((answer) => answer.assessmentId);
            const matchingAnswers = xAnswers.filter((answer) =>
              yAnswerIds.includes(answer.assessmentId),
            );

            return (
              <NineBoxCell
                key={xValue}
                label={
                  getNineBoxChartLabel(
                    chartLabels,
                    xQuestion.id,
                    xValue,
                    yQuestion?.id,
                    yValue,
                  )?.label
                }
                isFiltered={
                  !!answerFilterList.find(
                    (answerFilter) =>
                      answerFilter.questionId === xQuestion.id &&
                      answerFilter.integerAnswer === xValue,
                  ) &&
                  !!answerFilterList.find(
                    (answerFilter) =>
                      answerFilter.questionId === yQuestion.id &&
                      answerFilter.integerAnswer === yValue,
                  )
                }
                count={matchingAnswers.length}
                totalAssessments={xQuestionAnswers.length}
                people={peopleOf(matchingAnswers)}
                onApplyFilter={() =>
                  handleUpdateAnswerFilter(
                    { questionId: xQuestion.id, integerAnswer: xValue },
                    { questionId: yQuestion.id, integerAnswer: yValue },
                  )
                }
                onRemoveFilter={() => onChangeAnswerFilterList([])}
              />
            );
          }}
        />
      )}
      {xQuestion && !yQuestion && (
        <SingleQuestionNineBoxGrid
          question={xQuestion}
          cellRenderer={({ value }) => {
            const matchingAnswers = xQuestionAnswers.filter(
              (answer) => answer.integerAnswer === value,
            );

            return (
              <NineBoxCell
                key={value}
                label={
                  getNineBoxChartLabel(chartLabels, xQuestion.id, value)?.label
                }
                isFiltered={
                  !!answerFilterList.find(
                    (answerFilter) =>
                      answerFilter.questionId === xQuestion.id &&
                      answerFilter.integerAnswer === value,
                  )
                }
                count={matchingAnswers.length}
                totalAssessments={xQuestionAnswers.length}
                people={peopleOf(matchingAnswers)}
                onApplyFilter={() =>
                  handleUpdateAnswerFilter({
                    questionId: xQuestion.id,
                    integerAnswer: value,
                  })
                }
                onRemoveFilter={() => onChangeAnswerFilterList([])}
              />
            );
          }}
        />
      )}
    </div>
  );
};

export default PerformanceAssessmentChart;
